import { DefaultRootState } from 'react-redux';

import { startOfMonth } from 'date-fns';
import { CampaignSource, CampaignSubSource, SOURCE_FOR_STATS } from 'utils/constants/campaign';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

const STATISTICS_HEADER_RESPONSES = 'RESPONSES';

type CampaignId = number;
type BannerId = number;
type ObjectName = string;
type ShowResponsesAndCr = boolean;
type ShowCr = boolean;
type VacancyId = number;
type FromDate = Date;
type ToDate = Date;
type IsLoad = boolean;

type StatisticsItemDataKey = CampaignSubSource & typeof STATISTICS_HEADER_RESPONSES;

interface Summary {
    averagePricePerClickInCents?: number;
    charges?: number;
    clicks?: number;
    ctr?: number;
    cr?: number;
    impressions?: number;
    responses?: number;
}

interface StatisticsItem {
    date: string;
    data: Record<StatisticsItemDataKey, number>;
    summary: Summary;
}

interface Items {
    stats: StatisticsItem[];
    total: Omit<StatisticsItem, 'date'> | null;
}

// ----------------------- actions -------------------------------------
const SET_STATISTICS_BY_DATES = 'SET_STATISTICS_BY_DATES';
const SET_STATISTICS_DATES = 'SET_STATISTICS_DATES';
const SET_STATISTICS_IS_LOAD = 'SET_STATISTICS_IS_LOAD';
const SET_STATISTICS_CAMPAIGN_ID = 'SET_STATISTICS_CAMPAIGN_ID';
const SET_STATISTICS_BANNER_ID = 'SET_STATISTICS_BANNER_ID';
const SET_STATISTICS_OBJECT_NAME = 'SET_STATISTICS_OBJECT_NAME';
const SET_STATISTICS_SHOW_CR = 'SET_STATISTICS_SHOW_CR';
const SET_STATISTICS_SHOW_RESPONSES_AND_CR = 'SET_STATISTICS_SHOW_RESPONSES_AND_CR';
const SET_STATISTICS_VACANCY_ID = 'SET_STATISTICS_VACANCY_ID';
const SET_STATISTICS_HEADERS = 'SET_STATISTICS_HEADERS';
const SET_STATISTICS_USED_SUBSOURCES = 'SET_STATISTICS_USED_SUBSOURCES';

interface PayloadTypes {
    [SET_STATISTICS_BY_DATES]: Items;
    [SET_STATISTICS_DATES]: { from: FromDate; to: ToDate };
    [SET_STATISTICS_IS_LOAD]: IsLoad;
    [SET_STATISTICS_CAMPAIGN_ID]: CampaignId;
    [SET_STATISTICS_BANNER_ID]: BannerId;
    [SET_STATISTICS_OBJECT_NAME]: ObjectName;
    [SET_STATISTICS_SHOW_CR]: ShowCr;
    [SET_STATISTICS_SHOW_RESPONSES_AND_CR]: ShowResponsesAndCr;
    [SET_STATISTICS_VACANCY_ID]: VacancyId;
    [SET_STATISTICS_HEADERS]: CampaignSubSource[];
    [SET_STATISTICS_USED_SUBSOURCES]: { [key in CampaignSource]?: CampaignSubSource[] };
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setStatisticsByDates = ActionCreator(SET_STATISTICS_BY_DATES);
export const setStatisticsDates = ActionCreator(SET_STATISTICS_DATES);
export const setStatisticsLoad = ActionCreator(SET_STATISTICS_IS_LOAD);
export const setStatisticsCampaignId = ActionCreator(SET_STATISTICS_CAMPAIGN_ID);
export const setStatisticsBannerId = ActionCreator(SET_STATISTICS_BANNER_ID);
export const setStatisticsObjectName = ActionCreator(SET_STATISTICS_OBJECT_NAME);
export const setStatisticsShowCr = ActionCreator(SET_STATISTICS_SHOW_CR);
export const setStatisticsShowResponsesAndCr = ActionCreator(SET_STATISTICS_SHOW_RESPONSES_AND_CR);
export const setStatisticsVacancyId = ActionCreator(SET_STATISTICS_VACANCY_ID);
export const setStatisticsHeaders = ActionCreator(SET_STATISTICS_HEADERS);
export const setStatisticsUsedSubSources = ActionCreator(SET_STATISTICS_USED_SUBSOURCES);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
interface StatisticsInitialState {
    items: Items;
    isLoad: IsLoad;
    from: FromDate;
    to: ToDate;
    campaignId: CampaignId;
    bannerId: BannerId;
    objectName: ObjectName;
    showResponsesAndCr: ShowResponsesAndCr;
    showCr: ShowCr;
    vacancyId: VacancyId;
    headers: Array<CampaignSubSource | CampaignSource>;
    usedSubSources?: { [key in CampaignSource]?: CampaignSubSource[] };
}

export const STATISTICS_INITIAL_STATE: StatisticsInitialState = {
    items: { stats: [], total: null },
    isLoad: false,
    from: startOfMonth(new Date()),
    to: new Date(),
    campaignId: 0,
    bannerId: 0,
    objectName: '',
    showResponsesAndCr: false,
    showCr: false,
    vacancyId: 0,
    headers: SOURCE_FOR_STATS,
    usedSubSources: undefined,
};

export default createReducer<StatisticsInitialState, PayloadTypes>(STATISTICS_INITIAL_STATE, {
    [SET_STATISTICS_SHOW_CR]: (state, { payload }) => ({
        ...state,
        showCr: payload,
    }),
    [SET_STATISTICS_VACANCY_ID]: (state, { payload }) => ({
        ...state,
        vacancyId: payload,
    }),
    [SET_STATISTICS_BY_DATES]: (state, { payload }) => ({
        ...state,
        items: payload,
    }),
    [SET_STATISTICS_OBJECT_NAME]: (state, { payload }) => ({
        ...state,
        objectName: payload,
    }),
    [SET_STATISTICS_DATES]: (state, { payload }) => ({
        ...state,
        from: payload.from,
        to: payload.to,
    }),
    [SET_STATISTICS_IS_LOAD]: (state, { payload }) => ({
        ...state,
        isLoad: payload,
    }),
    [SET_STATISTICS_CAMPAIGN_ID]: (state, { payload }) => ({
        ...state,
        campaignId: payload,
    }),
    [SET_STATISTICS_BANNER_ID]: (state, { payload }) => ({
        ...state,
        bannerId: payload,
    }),
    [SET_STATISTICS_SHOW_RESPONSES_AND_CR]: (state, { payload }) => ({
        ...state,
        showResponsesAndCr: payload,
    }),
    [SET_STATISTICS_HEADERS]: (state, { payload }) => ({
        ...state,
        headers: payload,
    }),
    [SET_STATISTICS_USED_SUBSOURCES]: (state, { payload }) => ({
        ...state,
        usedSubSources: payload,
    }),
});
// ----------------------- /reducer --------------------------------------

export const selectIsAveragePricePerClickAvailable = (state: DefaultRootState): boolean => {
    const totalAveragePricePerClickInCents = state.statistics.items.total?.summary?.averagePricePerClickInCents;
    return !!totalAveragePricePerClickInCents || totalAveragePricePerClickInCents === 0;
};
